/**
 * `useSwaps` — TanStack Query hooks for the Feature B swap-request
 * endpoints (#305 backend, #306 SPA).
 *
 * Six hooks live here together because they all consume the same
 * generated types (`SwapRequestRowResponse` / `SwapRequestCreateBody` /
 * `SwapVetoBody`) and invalidate the same caches on mutation. Keeping
 * them in one module avoids the cross-import sprawl that hurts when a
 * future regen shifts the type shapes.
 *
 * Cache-key shape:
 *
 *   * `["me-swaps-outgoing", accountId, status?, direction?]`
 *   * `["me-swaps-incoming", accountId, cycleId?]`
 *   * `["cycle-swaps", cycleId, status?]`
 *
 * Mutations (`create`, `accept`, `veto`) invalidate **every** swap cache
 * key on success — first-mover semantics means a successful accept by
 * one client racing another should refresh both lists.
 *
 * Per `feedback_ci_green_and_issue_links.md` + the brief: no raw
 * `fetch()` — `useQuery` / `useMutation` throughout, sharing the
 * `apiFetch` wrapper with the rest of the SPA.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../lib/AuthContext";
import { weekdayShortLabel } from "../lib/dateWindow";
import { roleDisplay } from "../memberapp/calendar/calendarModel";
import { ApiError, apiFetch } from "./client";
import { liveRefetchOptions } from "./liveRefetch";
import { invalidateRosterReads } from "./rosterInvalidation";
import type { components } from "./schema.gen";

/**
 * `SwapRequestRow` — the lead-board / outgoing / incoming row shape.
 *
 * The merged backend (R3.10 #820) added `alt_dates` to
 * `SwapRequestRowResponse` for the board's WANTS column, but the
 * generated `schema.gen.ts` in this checkout predates that field. Per
 * the build brief we DON'T regen the schema — instead we intersect the
 * generated type with the hand-typed additive field. `alt_dates` is a
 * plain list of ISO dates (NOT a per-member or rate shape), so the
 * anti-Goodhart guard stays green. Keep in sync with the Pydantic model.
 */
export type SwapRequestRow = components["schemas"]["SwapRequestRowResponse"] & {
  /** R3.10 (#820) — days the requester could swap *into* ("give up
   *  {shift}, wants {alt_dates}"). Empty = broadcast-to-all-eligible. */
  alt_dates?: string[];
  /** #1603 — `member` (ordinary swap) | `mc_cover` (lead-initiated cover
   *  ask for a teammate's sick day). Drives the "Cover ask" labeling. */
  origin?: string;
};
export type SwapRequestCreateBody = components["schemas"]["SwapRequestCreateBody"];
export type SwapVetoBody = components["schemas"]["SwapVetoBody"];
export type CycleSwapsResponse = components["schemas"]["CycleSwapsResponse"];
export type SwapStateSummary = components["schemas"]["SwapStateSummary"];
export type SwapEligibilityPreview = components["schemas"]["SwapEligibilityPreview"];
// R3.10 (#820) — the lead-side cover-change picker + commit shapes.
// Already present in the generated envelope (the backend shipped them in
// #522), so they're reused here rather than hand-typed.
export type CoverEligibleResponse = components["schemas"]["CoverEligibleResponse"];
export type CoverEligibleMember = components["schemas"]["CoverEligibleMember"];
export type LeadCoverChangeBody = components["schemas"]["LeadCoverChangeBody"];

/**
 * Hand-typed mirror of the merged backend's `SwapEventEntry`
 * (`backend/src/api/routes/swaps.py`, R3.10 #820). One state-transition
 * event in a single swap's audit timeline — scoped to ONE swap, never a
 * cross-swap per-member rollup, so the anti-Goodhart guard holds (no
 * count / rate / streak column; each entry is a typed event + the actor's
 * display name + a timestamp). NOT in `schema.gen.ts` (per the build
 * brief, hand-typed); keep in sync with the Pydantic model.
 */
export interface SwapEventEntry {
  id: string;
  /** `requested | broadcast | accepted | committed | vetoed | expired |
   *  withdrawn | declined` (CHECK-pinned on the substrate). */
  event_type: string;
  actor_account_id: string;
  actor_display_name?: string | null;
  created_at: string;
  /** #1766 — the stored per-transition context, verbatim. An `accepted`
   *  event on a held swap carries `regressions` (the accept-time mix
   *  detail); a lead-confirmed `committed` event carries
   *  `applied_mix_regressions` (the fresh set the confirm applied).
   *  Single-transition facts only — never a count/rate/streak shape. */
  payload?: Record<string, unknown>;
}

/** Hand-typed mirror of the merged backend's `SwapEventsResponse`. */
export interface SwapEventsResponse {
  swap_id: string;
  events: SwapEventEntry[];
}

// ─── Shift labels (shared by every swap surface) ─────────────────────

/**
 * The shift a swap is about, in the words the rest of the product uses.
 *
 * A swap row carries the persistence role token (`STATION_55_AM`) and an
 * ISO date. Every other surface a member or lead reads — the roster list,
 * the roster cell, the member calendar — renders those through
 * `roleDisplay()` plus a weekday-anchored short date, so a swap that said
 * `2026-11-03` + `STATION_55_AM` was the one place in the app speaking in
 * identifiers. These helpers are the single source of that copy for the
 * raise dialog, the member's own rows, the incoming offers and the lead's
 * board.
 *
 * `session` is the assignment's own session label; it is used only when
 * the role token carries no session of its own, so a `STATION_55_AM` row
 * never reads "55 · AM · AM".
 */
export function swapRoleLabel(role: string, session?: string | null): string {
  const display = roleDisplay(role);
  const fallback = session != null && session.trim() !== "" ? session.trim() : null;
  const sessionLabel = display.session ?? fallback;
  return sessionLabel ? `${display.station} · ${sessionLabel}` : display.station;
}

/** `"Tue 3 Nov · 55 · AM"` — date + role + session as one phrase. */
export function swapShiftLabel(assignment: {
  date: string;
  role: string;
  session?: string | null;
}): string {
  return `${weekdayShortLabel(assignment.date)} · ${swapRoleLabel(assignment.role, assignment.session)}`;
}

// ─── Taking an ask (shared by the ask screen and the Inbox card) ─────
//
// #1923 — the Inbox card's `Take it` accepts in place (D9), so the words
// around an accept live here rather than in `MeSwaps`: the ask screen and
// the card must say the same thing before, during and after the same
// `useSwapAccept` call.

/**
 * Whether a session label is an on-call one. The heuristic mirrors
 * `SwapRequestModal.isOnCallSession` so the trigger, the modal and every
 * accept surface agree on what counts as on-call.
 */
export function isOnCallSwapSession(session: string | null | undefined): boolean {
  return (session ?? "").toLowerCase().includes("on-call");
}

/** `"Accepting adds 1 shift to your roster."` — the ask-once sentence. */
export function swapAcceptImpactLine(session: string | null | undefined): string {
  return isOnCallSwapSession(session)
    ? "Accepting adds 1 night-call to your roster."
    : "Accepting adds 1 shift to your roster.";
}

/** `"You're now on 55 · AM, Tue 3 Nov — it's on your calendar."` */
export function swapAcceptedLine(assignment: {
  date: string;
  role: string;
  session?: string | null;
}): string {
  const shift = `${swapRoleLabel(assignment.role, assignment.session)}, ${weekdayShortLabel(assignment.date)}`;
  return `You're now on ${shift} — it's on your calendar.`;
}

/**
 * #1698 (CC3, stamped OQ-7) — what an accept that came back HELD for the
 * lead's confirmation says. Aggregate framing only: the shift's people-mix,
 * never levels or numbers.
 */
export function swapHeldNotice(assignment: {
  date: string;
  role: string;
  session?: string | null;
}): string {
  return `Sent to your lead to confirm — taking the ${swapShiftLabel(assignment)} shift changes the mix of people on it, so your lead confirms this one first. It will appear on your roster once confirmed.`;
}

/** Whether an accept came back held for the lead rather than committed. */
export function isHeldAccept(row: Pick<SwapRequestRow, "status" | "hold_reason_code">): boolean {
  return row.status === "accepted" && row.hold_reason_code != null;
}

/** #2259 — the accept's 409 when the lead's change of the seat (a grid edit
 * or Cover-change) closed the ask. Telegram says the same words. */
export const SWAP_CLOSED_BY_LEAD_TEXT = "Your lead changed this day, so this swap was closed.";
const SWAP_TAKEN_BY_PEER_TEXT =
  "Another teammate accepted this swap a moment ago. We'll refresh the list.";
/** The API's own 409 sentences (`_pending_seat_guard.SEAT_CHANGE_PENDING_MEMBER`,
 * `middleware/errors.TRY_AGAIN_DETAIL`), matched by their opening words. */
const SEAT_PENDING_PREFIX = "Your lead is changing this day.";
const TRY_AGAIN_PREFIX = "Someone changed this at the same moment";

/** An accept that failed, in the member's words (409 race / 422 rules). */
export function swapAcceptErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 409) {
      // #2259 — a 409 is not always a teammate's accept: the lead changed the
      // day, the seat waits for the lead's Publish, or two changes met at the
      // same moment. The last two arrive already in words (the API's
      // sentences, shown as they are); anything else reads as the teammate
      // race, as before.
      const detail = err.detail;
      if (detail === "swap_closed_by_lead") return SWAP_CLOSED_BY_LEAD_TEXT;
      if (detail.startsWith(SEAT_PENDING_PREFIX) || detail.startsWith(TRY_AGAIN_PREFIX)) {
        return detail;
      }
      return SWAP_TAKEN_BY_PEER_TEXT;
    }
    if (err.status === 422) {
      return err.detail || "This swap is no longer feasible (rules may have changed).";
    }
    return err.detail;
  }
  return (err as Error).message;
}

const STALE_TIME_MS = 30_000;

export function meSwapsOutgoingKey(
  accountId: string | null,
  status?: string | null,
  direction?: string | null,
) {
  return ["me-swaps-outgoing", accountId ?? "", status ?? "", direction ?? ""] as const;
}

export function meSwapsIncomingKey(accountId: string | null, cycleId?: string | null) {
  return ["me-swaps-incoming", accountId ?? "", cycleId ?? ""] as const;
}

export function cycleSwapsKey(cycleId: string, status?: string | null) {
  return ["cycle-swaps", cycleId, status ?? ""] as const;
}

export function meSwapEligibilityKey(
  accountId: string | null,
  assignmentId: string | null,
  altDates: readonly string[],
) {
  // Stable key — sort alt-dates so caller order doesn't fragment the cache.
  const sorted = [...altDates].sort();
  return ["me-swap-eligibility", accountId ?? "", assignmentId ?? "", sorted.join(",")] as const;
}

export function coverEligibleKey(cycleId: string, assignmentId: string | null) {
  return ["cover-eligible", cycleId, assignmentId ?? ""] as const;
}

export function swapEventsKey(cycleId: string, swapId: string | null) {
  return ["swap-events", cycleId, swapId ?? ""] as const;
}

/** A swap that moved a shift: the swap caches and every roster read (the
 *  grid and the lead's day verdict, #2173) — an accept, a lead's cover
 *  change, a held swap confirmed. */
function invalidateAfterSwapApplied(
  queryClient: ReturnType<typeof useQueryClient>,
  cycleId?: string,
): Promise<unknown> {
  return Promise.all([
    invalidateAllSwapCaches(queryClient),
    invalidateRosterReads(queryClient, cycleId),
  ]);
}

function invalidateAllSwapCaches(queryClient: ReturnType<typeof useQueryClient>): Promise<unknown> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ["me-swaps-outgoing"] }),
    queryClient.invalidateQueries({ queryKey: ["me-swaps-incoming"] }),
    queryClient.invalidateQueries({ queryKey: ["cycle-swaps"] }),
    queryClient.invalidateQueries({ queryKey: ["me-schedule"] }),
    queryClient.invalidateQueries({ queryKey: ["me-schedule-horizon"] }),
    // #1909 — the member inbox carries the `swap_ask` / `cover_ask` card
    // whose "Take it" sends this accept (#1923). Resolving the swap resolves
    // that card server-side, but without this the NEEDS YOU strip kept
    // offering it until a full page reload. The member calendar reads
    // `me-schedule-window`, which must re-read too — the shift they just
    // took is now theirs.
    queryClient.invalidateQueries({ queryKey: ["member-inbox"] }),
    queryClient.invalidateQueries({ queryKey: ["member-inbox-archive"] }),
    queryClient.invalidateQueries({ queryKey: ["me-schedule-window"] }),
  ]);
}

// ─── Queries ─────────────────────────────────────────────────────────

export function useMeSwapsOutgoing(opts?: { status?: string | null; direction?: string | null }) {
  const { accountId } = useAuth();
  const status = opts?.status ?? null;
  const direction = opts?.direction ?? null;
  return useQuery<SwapRequestRow[], ApiError | Error>({
    queryKey: meSwapsOutgoingKey(accountId, status, direction),
    enabled: Boolean(accountId),
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (direction) params.set("direction", direction);
      const qs = params.toString();
      const suffix = qs ? `?${qs}` : "";
      return apiFetch<SwapRequestRow[]>(`/api/account/me/swaps${suffix}`);
    },
  });
}

export function useMeSwapsIncoming(opts?: { cycleId?: string | null }) {
  const { accountId } = useAuth();
  const cycleId = opts?.cycleId ?? null;
  return useQuery<SwapRequestRow[], ApiError | Error>({
    queryKey: meSwapsIncomingKey(accountId, cycleId),
    enabled: Boolean(accountId),
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => {
      const params = new URLSearchParams();
      if (cycleId) params.set("cycle_id", cycleId);
      const qs = params.toString();
      const suffix = qs ? `?${qs}` : "";
      return apiFetch<SwapRequestRow[]>(`/api/account/me/swaps/incoming${suffix}`);
    },
  });
}

/**
 * `useCycleSwaps` — the lead's swaps board read (`GET /api/cycles/:cid/swaps`,
 * lead-only, 404 when the team has `swap_requests` off).
 *
 * `enabled` (default true) lets a caller that is not the board hold the
 * read back until it knows the caller is a lead on a team with the
 * component on — the workspace's `Swaps` chip (#1909) passes it so a member
 * or an off-team never fetches. The query key is the board's either way, so
 * the chip and the board share one cache entry.
 *
 * Live read (flows finding 36). A swap is raised, taken, declined or
 * withdrawn from a MEMBER's phone, so no mutation in the lead's window ever
 * invalidates this key — and `refetchOnWindowFocus` is off app-wide. The
 * chip therefore never appeared after a raise and still read "1 open" after
 * the take until a reload. It now opts into the same focus refetch + 30s
 * visible-tab poll as the other live-roster reads (`liveRefetch.ts`); the
 * lead's own board actions still invalidate it at once via
 * `invalidateAllSwapCaches`.
 */
export function useCycleSwaps(
  cycleId: string | undefined,
  opts?: { status?: string | null; enabled?: boolean },
) {
  const { accountId } = useAuth();
  const status = opts?.status ?? null;
  return useQuery<CycleSwapsResponse, ApiError | Error>({
    queryKey: cycleSwapsKey(cycleId ?? "", status),
    enabled: (opts?.enabled ?? true) && Boolean(accountId && cycleId),
    staleTime: STALE_TIME_MS,
    retry: false,
    ...liveRefetchOptions,
    queryFn: () => {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      const qs = params.toString();
      const suffix = qs ? `?${qs}` : "";
      return apiFetch<CycleSwapsResponse>(`/api/cycles/${cycleId}/swaps${suffix}`);
    },
  });
}

/**
 * `useCoverEligible` — R3.10 (#820). The lead-side eligible-peer picker.
 *
 * `GET /api/cycles/:cid/swaps/cover-eligible?assignment_id=` → names +
 * membership ids of peers who can take a slot via a lead cover-change
 * (the same matcher the member swap flow uses). Lead-only, gated
 * `swap_requests`.
 *
 * Privacy posture (anti-Goodhart): the board's list rows carry only the
 * eligible-pool COUNT (`eligible_pool_size`). NAMES are fetched on demand
 * here — only when the lead deliberately acts on one specific swap — so
 * the privacy boundary holds and the board never aggregates per member.
 * The query is therefore disabled until `enabled` is set true by the
 * lead opening a picker for a specific assignment.
 */
export function useCoverEligible(opts: {
  cycleId: string;
  assignmentId: string | null;
  enabled?: boolean;
}) {
  const { accountId } = useAuth();
  const { cycleId, assignmentId } = opts;
  const enabled = (opts.enabled ?? false) && Boolean(accountId && cycleId && assignmentId);
  return useQuery<CoverEligibleResponse, ApiError | Error>({
    queryKey: coverEligibleKey(cycleId, assignmentId),
    enabled,
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () =>
      apiFetch<CoverEligibleResponse>(
        `/api/cycles/${cycleId}/swaps/cover-eligible?assignment_id=${assignmentId}`,
      ),
  });
}

/**
 * `useSwapEvents` — R3.10 (#820) lead-side "Monitor" timeline.
 *
 * `GET /api/cycles/:cid/swaps/:id/events` → the ordered audit timeline
 * for ONE swap ("requested → broadcast → declined (Tom) → accepted
 * (Aisha) → committed"). Lead-only, gated `swap_requests`. Scoped to a
 * single swap — never a cross-swap per-member rollup — so the
 * anti-Goodhart guard holds. Disabled until the lead opens the Monitor
 * drawer for a specific swap.
 */
export function useSwapEvents(opts: {
  cycleId: string;
  swapId: string | null;
  enabled?: boolean;
}) {
  const { accountId } = useAuth();
  const { cycleId, swapId } = opts;
  const enabled = (opts.enabled ?? false) && Boolean(accountId && cycleId && swapId);
  return useQuery<SwapEventsResponse, ApiError | Error>({
    queryKey: swapEventsKey(cycleId, swapId),
    enabled,
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => apiFetch<SwapEventsResponse>(`/api/cycles/${cycleId}/swaps/${swapId}/events`),
  });
}

/**
 * `useSwapEligibilityPreview` — #349 §9.4 v0.2 pre-submit count.
 *
 * Powers the SwapRequestModal's inline "N teammates are eligible…" line
 * that the alt-dates picker drives. The query is debounced at the call
 * site (the modal holds the alt-dates state and only updates this hook's
 * `altDates` input after a delay).
 *
 * The endpoint returns COUNT only — names/ids/shifts are never returned.
 * Privacy boundary: pinned by `test_me_swaps_eligibility_preview.py`.
 *
 * #1684 — `placeholderData: keepPreviousData`. The alt-dates are part of
 * the cache key, so every date the member taps mints a fresh key; without
 * a placeholder `data` fell to `undefined` for the ~300ms of the refetch
 * and the modal's policy banner unmounted and remounted on every tap
 * (founder-reported flicker + repeated `aria-live` announcements). The
 * response's policy half (`swap_allowed` / `blocked_reason` /
 * `min_notice_days`) does NOT depend on the alt-dates, so carrying the
 * previous response through the refetch is always truthful; the count
 * half is momentarily stale, which the modal marks via `isFetching`.
 */
export function useSwapEligibilityPreview(opts: {
  assignmentId: string | null;
  altDates: readonly string[];
  enabled?: boolean;
}) {
  const { accountId } = useAuth();
  const { assignmentId, altDates } = opts;
  const enabled = (opts.enabled ?? true) && Boolean(accountId && assignmentId);
  return useQuery<SwapEligibilityPreview, ApiError | Error>({
    queryKey: meSwapEligibilityKey(accountId, assignmentId, altDates),
    enabled,
    staleTime: STALE_TIME_MS,
    placeholderData: keepPreviousData,
    retry: false,
    queryFn: () => {
      const params = new URLSearchParams();
      if (assignmentId) params.set("assignment_id", assignmentId);
      for (const d of altDates) params.append("alt_dates", d);
      return apiFetch<SwapEligibilityPreview>(
        `/api/account/me/swaps/eligibility?${params.toString()}`,
      );
    },
  });
}

// ─── Mutations ───────────────────────────────────────────────────────

export function useSwapRequestCreate() {
  const queryClient = useQueryClient();
  return useMutation<SwapRequestRow, ApiError | Error, SwapRequestCreateBody>({
    mutationFn: (body) =>
      apiFetch<SwapRequestRow>("/api/account/me/swaps", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidateAllSwapCaches(queryClient),
  });
}

export function useSwapAccept() {
  const queryClient = useQueryClient();
  return useMutation<SwapRequestRow, ApiError | Error, { swapId: string }>({
    mutationFn: ({ swapId }) =>
      apiFetch<SwapRequestRow>(`/api/account/me/swaps/${swapId}/accept`, {
        method: "POST",
      }),
    onSuccess: () => invalidateAfterSwapApplied(queryClient),
  });
}

export function useSwapVeto() {
  const queryClient = useQueryClient();
  return useMutation<
    SwapRequestRow,
    ApiError | Error,
    { cycleId: string; swapId: string; body: SwapVetoBody }
  >({
    mutationFn: ({ cycleId, swapId, body }) =>
      apiFetch<SwapRequestRow>(`/api/cycles/${cycleId}/swaps/${swapId}/veto`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidateAllSwapCaches(queryClient),
  });
}

/**
 * `useLeadCoverChange` — R3.10 (#820). The lead reassigns a slot to a
 * chosen eligible peer (#522).
 *
 * `POST /api/cycles/:cid/swaps/cover-change` with `{ assignment_id,
 * cover_membership_id, note? }`. Applied directly: the lead has
 * reassignment authority, so the swap is created and driven straight to
 * `committed` in one transaction (the board's "Commit" / "Assign cover"
 * action). There is no separate lead-commit of an already-accepted swap
 * — a member accept already commits — so this is the lead's terminal
 * commit path. Invalidate-on-success so the board re-reads the moved
 * assignment + the new state counts.
 */
export function useLeadCoverChange(cycleId: string) {
  const queryClient = useQueryClient();
  return useMutation<SwapRequestRow, ApiError | Error, LeadCoverChangeBody>({
    mutationFn: (body) =>
      apiFetch<SwapRequestRow>(`/api/cycles/${cycleId}/swaps/cover-change`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidateAfterSwapApplied(queryClient, cycleId),
  });
}

/**
 * `useSwapHoldConfirm` / `useSwapHoldDecline` — #1698 (CC3, stamped
 * OQ-7). Lead actions on a swap accept HELD for confirmation
 * (`status="accepted"` + `hold_reason_code="attribute_mix"` — the
 * accept would push a previously-met attribute condition below its
 * threshold, so the roster stays untouched until the lead rules).
 *
 *   * Confirm — `POST /api/cycles/:cid/swaps/:id/confirm`: applies the
 *     swap exactly as a direct accept would (assignment moves, row
 *     commits). The backend re-runs the feasibility recheck first; a
 *     stale hold answers the same stable 422 the accept path uses and
 *     the row STAYS held so the lead can decline instead.
 *   * Decline — `POST /api/cycles/:cid/swaps/:id/decline`: restores the
 *     pre-accept state (row back to `broadcast`, accepter + hold
 *     cleared, roster untouched) so other eligible peers can still
 *     take the shift.
 *
 * Invalidate-on-success across every swap cache — the board re-reads
 * the new state and the accepter's/requester's member lists move too.
 */
export function useSwapHoldConfirm(cycleId: string) {
  const queryClient = useQueryClient();
  return useMutation<SwapRequestRow, ApiError | Error, { swapId: string }>({
    mutationFn: ({ swapId }) =>
      apiFetch<SwapRequestRow>(`/api/cycles/${cycleId}/swaps/${swapId}/confirm`, {
        method: "POST",
      }),
    onSuccess: () => invalidateAfterSwapApplied(queryClient, cycleId),
  });
}

export function useSwapHoldDecline(cycleId: string) {
  const queryClient = useQueryClient();
  return useMutation<SwapRequestRow, ApiError | Error, { swapId: string }>({
    mutationFn: ({ swapId }) =>
      apiFetch<SwapRequestRow>(`/api/cycles/${cycleId}/swaps/${swapId}/decline`, {
        method: "POST",
      }),
    onSuccess: () => invalidateAllSwapCaches(queryClient),
  });
}

/**
 * `useSwapWithdraw` — R4.6 (#820/#916). The caller pulls back their OWN
 * outgoing swap request before any peer has committed.
 *
 * `POST /api/account/me/swaps/{id}/withdraw` → the updated
 * `SwapRequestRowResponse` with `status: "withdrawn"`. The backend only
 * permits this from `pending|broadcast` (a 409 surfaces at the call site
 * when a peer already committed). No assignment moves — pure state
 * transition. Invalidate-on-success so the outgoing list re-reads the
 * new status (consistent with the other swap mutations).
 */
export function useSwapWithdraw() {
  const queryClient = useQueryClient();
  return useMutation<SwapRequestRow, ApiError | Error, { swapId: string }>({
    mutationFn: ({ swapId }) =>
      apiFetch<SwapRequestRow>(`/api/account/me/swaps/${swapId}/withdraw`, {
        method: "POST",
      }),
    onSuccess: () => invalidateAllSwapCaches(queryClient),
  });
}

/**
 * `useSwapDecline` — R4.6 (#820/#916). The caller declines an INCOMING
 * offer they're eligible for.
 *
 * `POST /api/account/me/swaps/{id}/decline` → `204 No Content`. A single
 * peer declining does NOT kill the broadcast — the offer stays open for
 * other eligible peers; the backend writes a per-peer `declined` audit
 * event and the incoming-list query already excludes the caller's own
 * declines server-side. So on success the row simply drops off the
 * caller's incoming feed once the cache re-reads. The requester is
 * notified by the backend; the SPA doesn't handle notification.
 */
export function useSwapDecline() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError | Error, { swapId: string }>({
    mutationFn: ({ swapId }) =>
      apiFetch<void>(`/api/account/me/swaps/${swapId}/decline`, {
        method: "POST",
      }),
    onSuccess: () => invalidateAllSwapCaches(queryClient),
  });
}
